import { useEffect, useState } from 'react';

import { LANG_CODES, parseDirection, type Stats } from '@lexio/shared';

import { getStats } from '../lib/api';

export function StatsPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void getStats()
      .then(setStats)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="center muted">Načítám…</div>;
  if (!stats) return <div className="center muted">Statistiky se nepodařilo načíst.</div>;

  const goalPct = Math.min(Math.round((stats.reviewedToday / stats.dailyGoal) * 100), 100);
  const maxDay = Math.max(...stats.history.map((h) => h.count), 1);

  return (
    <div className="stats">
      <div className="stat-grid">
        <div className="stat-tile">
          <div className="stat-value">{stats.streakDays}</div>
          <div className="stat-label">dní v řadě</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value">
            {stats.reviewedToday}
            <span className="stat-sub">/{stats.dailyGoal}</span>
          </div>
          <div className="stat-label">dnes zkoušeno</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value">{stats.dueNow}</div>
          <div className="stat-label">čeká teď</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value">
            {stats.retention30d === null ? '—' : `${Math.round(stats.retention30d * 100)}%`}
          </div>
          <div className="stat-label">úspěšnost 30 dní</div>
        </div>
      </div>

      <section>
        <h3>Denní cíl</h3>
        <div className="progress-bar big">
          <div className="progress-fill" style={{ width: `${goalPct}%` }} />
        </div>
        <p className="muted small">
          {stats.reviewedToday >= stats.dailyGoal
            ? 'Cíl splněn 👌'
            : `Do cíle zbývá ${stats.dailyGoal - stats.reviewedToday} karet.`}
        </p>
      </section>

      <section>
        <h3>Posledních 30 dní</h3>
        <div className="chart">
          {stats.history.map((h) => (
            <div
              key={h.date}
              className={`bar ${h.count >= stats.dailyGoal ? 'goal' : ''}`}
              style={{ height: `${Math.max((h.count / maxDay) * 100, 2)}%` }}
              title={`${h.date}: ${h.count}`}
            />
          ))}
        </div>
      </section>

      {stats.byDirection.length > 0 && (
        <section>
          <h3>Podle směru</h3>
          <ul className="breakdown">
            {stats.byDirection.map((d) => {
              const dir = parseDirection(d.direction);
              return (
                <li key={d.direction}>
                  <span>
                    {dir ? `${LANG_CODES[dir.from]} → ${LANG_CODES[dir.to]}` : d.direction}
                    {d.due > 0 && <span className="due-badge">{d.due} čeká</span>}
                  </span>
                  <strong>{d.total}</strong>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section>
        <h3>Rozložení karet</h3>
        <ul className="breakdown">
          <li>
            <span>Nové</span>
            <strong>{stats.byState.new}</strong>
          </li>
          <li>
            <span>V učení</span>
            <strong>{stats.byState.learning + stats.byState.relearning}</strong>
          </li>
          <li>
            <span>Zafixované</span>
            <strong>{stats.byState.review}</strong>
          </li>
          <li>
            <span>Konceptů v databázi</span>
            <strong>{stats.totalConcepts}</strong>
          </li>
          <li>
            <span>Zítra ke zopakování</span>
            <strong>{stats.dueTomorrow}</strong>
          </li>
        </ul>
      </section>
    </div>
  );
}
